/**
 * ICloudContactsAdapter - iCloud Contacts API adapter
 * 
 * CHANGE HISTORY:
 * - 251030_0128 [pending] danielbeer: Moved to Interactr from Synkr (better architecture)
 * - 251029_2333 [pending] danielbeer: iCloud Contacts adapter using CardDAV protocol
 * 
 * Uses:
 * - Interactr for HTTP requests
 * - Authr for Apple ID authentication
 * - CardDAV protocol (WebDAV-based)
 * - vCard format for contact data
 */

import { BaseApiAdapter } from './BaseApiAdapter.js';
import type { ContactChange } from './types.js';

export interface ICloudContactsConfig {
  interactr: any; // InteractrManager instance
  username: string; // Apple ID email
  password: string; // App-specific password
  keystoreRef?: {
    provider: string;
    path: string;
    key: string;
  };
}

export class ICloudContactsAdapter extends BaseApiAdapter {
  private config: ICloudContactsConfig;
  private interactr: any;
  private cardDavUrl = 'https://contacts.icloud.com';
  private principalUrl: string | null = null;
  private addressBookUrl: string | null = null;

  constructor(config: ICloudContactsConfig) {
    super('icloud-contacts');
    this.config = config;
    this.interactr = config.interactr;
  }

  /**
   * Initialize adapter - discover CardDAV URLs
   */
  async initialize(): Promise<void> {
    if (!this.interactr) {
      throw new Error('Interactr not configured');
    }

    // Step 1: Discover principal URL
    await this.discoverPrincipal();

    // Step 2: Discover address book URL
    await this.discoverAddressBook();
  }

  /**
   * Discover CardDAV principal URL
   */
  private async discoverPrincipal(): Promise<void> {
    const request = await this.interactr.createRequest({
      name: 'iCloud CardDAV - Discover Principal',
      method: 'PROPFIND',
      url: `${this.cardDavUrl}/`,
      headers: {
        'Depth': '0',
        'Content-Type': 'application/xml; charset=utf-8',
      },
      body: {
        type: 'raw',
        content: `<?xml version="1.0" encoding="utf-8"?>
<d:propfind xmlns:d="DAV:">
  <d:prop>
    <d:current-user-principal/>
  </d:prop>
</d:propfind>`,
      },
      auth: {
        type: 'basic',
        config: {
          username: this.config.username,
          password: this.config.password,
        },
        keystoreRef: this.config.keystoreRef,
      },
      tags: ['icloud', 'contacts', 'carddav'],
      createdBy: 'synkr',
      updatedBy: 'synkr',
    });

    const response = await this.interactr.executeRequest(request.id);

    if (response.status !== 207) {
      throw new Error(`CardDAV principal discovery failed: ${response.status}`);
    }

    // Parse XML response to get principal URL
    // TODO: Implement XML parsing
    // For now, use known iCloud structure
    this.principalUrl = `${this.cardDavUrl}/${this.config.username.split('@')[0]}/principal/`;
  }

  /**
   * Discover address book URL
   */
  private async discoverAddressBook(): Promise<void> {
    if (!this.principalUrl) {
      throw new Error('Principal URL not discovered');
    }

    const request = await this.interactr.createRequest({
      name: 'iCloud CardDAV - Discover Address Book',
      method: 'PROPFIND',
      url: this.principalUrl,
      headers: {
        'Depth': '1',
        'Content-Type': 'application/xml; charset=utf-8',
      },
      body: {
        type: 'raw',
        content: `<?xml version="1.0" encoding="utf-8"?>
<d:propfind xmlns:d="DAV:" xmlns:card="urn:ietf:params:xml:ns:carddav">
  <d:prop>
    <card:addressbook-home-set/>
  </d:prop>
</d:propfind>`,
      },
      auth: {
        type: 'basic',
        config: {
          username: this.config.username,
          password: this.config.password,
        },
        keystoreRef: this.config.keystoreRef,
      },
      tags: ['icloud', 'contacts', 'carddav'],
      createdBy: 'synkr',
      updatedBy: 'synkr',
    });

    const response = await this.interactr.executeRequest(request.id);

    if (response.status !== 207) {
      throw new Error(`CardDAV address book discovery failed: ${response.status}`);
    }

    // Parse XML response to get address book URL
    // For now, use known iCloud structure
    this.addressBookUrl = `${this.cardDavUrl}/${this.config.username.split('@')[0]}/carddavhome/card/`;
  }

  /**
   * Pull changes from iCloud Contacts
   */
  async pull(lastSync?: number): Promise<ContactChange[]> {
    if (!this.addressBookUrl) {
      await this.initialize();
    }

    try {
      // Get all contacts using CardDAV REPORT
      const request = await this.interactr.createRequest({
        name: 'iCloud CardDAV - Get Contacts',
        method: 'REPORT',
        url: this.addressBookUrl!,
        headers: {
          'Depth': '1',
          'Content-Type': 'application/xml; charset=utf-8',
        },
        body: {
          type: 'raw',
          content: `<?xml version="1.0" encoding="utf-8"?>
<card:addressbook-query xmlns:d="DAV:" xmlns:card="urn:ietf:params:xml:ns:carddav">
  <d:prop>
    <d:getetag/>
    <card:address-data/>
  </d:prop>
</card:addressbook-query>`,
        },
        auth: {
          type: 'basic',
          config: {
            username: this.config.username,
            password: this.config.password,
          },
          keystoreRef: this.config.keystoreRef,
        },
        tags: ['icloud', 'contacts', 'carddav'],
        createdBy: 'synkr',
        updatedBy: 'synkr',
      });

      const response = await this.interactr.executeRequest(request.id);

      if (response.status !== 207) {
        throw new Error(`CardDAV contacts query failed: ${response.status}`);
      }

      // Parse XML response and extract vCards
      // TODO: Implement proper XML and vCard parsing
      // For now, return empty array as placeholder
      const changes: ContactChange[] = [];

      // Example vCard parsing (simplified)
      const vcards = this.parseVCardsFromXML(response.body);

      for (const vcard of vcards) {
        const contact = this.parseVCard(vcard);
        
        // Only include if modified after lastSync
        if (lastSync && contact.timestamp <= lastSync) {
          continue;
        }

        changes.push({
          id: contact.id,
          type: 'update' as const,
          data: contact.data,
          timestamp: contact.timestamp,
          source: 'icloud-contacts',
        });
      }

      return changes;
    } catch (error) {
      throw new Error(`Failed to pull from iCloud Contacts: ${error.message}`);
    }
  }

  /**
   * Push change to iCloud Contacts
   */
  async push(change: ContactChange): Promise<boolean> {
    if (!this.addressBookUrl) {
      await this.initialize();
    }

    try {
      // Convert contact to vCard format
      const vcard = this.toVCard(change.data);

      // Create or update contact using CardDAV PUT
      const url = `${this.addressBookUrl}${change.id}.vcf`;
      const method = change.type === 'create' ? 'PUT' : 'PUT';

      const request = await this.interactr.createRequest({
        name: `iCloud CardDAV - ${change.type === 'create' ? 'Create' : 'Update'} Contact`,
        method,
        url,
        headers: {
          'Content-Type': 'text/vcard; charset=utf-8',
        },
        body: {
          type: 'raw',
          content: vcard,
        },
        auth: {
          type: 'basic',
          config: {
            username: this.config.username,
            password: this.config.password,
          },
          keystoreRef: this.config.keystoreRef,
        },
        tags: ['icloud', 'contacts', 'carddav'],
        createdBy: 'synkr',
        updatedBy: 'synkr',
      });

      const response = await this.interactr.executeRequest(request.id);

      return response.status === 201 || response.status === 204;
    } catch (error) {
      console.error(`Failed to push to iCloud Contacts: ${error.message}`);
      return false;
    }
  }

  /**
   * Parse vCards from XML response
   */
  private parseVCardsFromXML(xml: string): string[] {
    // TODO: Implement proper XML parsing
    // For now, return empty array
    return [];
  }

  /**
   * Parse vCard to contact data
   */
  private parseVCard(vcard: string): { id: string; data: any; timestamp: number } {
    // TODO: Implement proper vCard parsing
    // This is a simplified placeholder
    
    const lines = vcard.split('\n');
    const data: any = {
      displayName: '',
      firstName: '',
      lastName: '',
      emails: [],
      phones: [],
      addresses: [],
      source: 'icloud',
      syncSource: 'icloud',
    };

    let id = '';

    for (const line of lines) {
      const [key, ...valueParts] = line.split(':');
      const value = valueParts.join(':').trim();

      if (key.startsWith('UID')) {
        id = value;
      } else if (key.startsWith('FN')) {
        data.displayName = value;
      } else if (key.startsWith('N')) {
        const [lastName, firstName] = value.split(';');
        data.lastName = lastName;
        data.firstName = firstName;
      } else if (key.startsWith('EMAIL')) {
        data.emails.push({
          id: `email-${data.emails.length}`,
          email: value,
          type: key.includes('WORK') ? 'work' : 'personal',
          isPrimary: data.emails.length === 0,
        });
      } else if (key.startsWith('TEL')) {
        data.phones.push({
          id: `phone-${data.phones.length}`,
          number: value,
          type: key.includes('CELL') ? 'mobile' : key.includes('WORK') ? 'work' : 'home',
          isPrimary: data.phones.length === 0,
        });
      }
    }

    return {
      id: id || `icloud-${Date.now()}`,
      data,
      timestamp: Date.now(),
    };
  }

  /**
   * Convert contact to vCard format
   */
  private toVCard(contact): string {
    const lines: string[] = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `UID:${contact.id || `icloud-${Date.now()}`}`,
      `FN:${contact.displayName || ''}`,
      `N:${contact.lastName || ''};${contact.firstName || ''};;;`,
    ];

    // Add emails
    if (contact.emails) {
      for (const email of contact.emails) {
        const type = email.type === 'work' ? 'WORK' : 'HOME';
        lines.push(`EMAIL;TYPE=${type}:${email.email}`);
      }
    }

    // Add phones
    if (contact.phones) {
      for (const phone of contact.phones) {
        const type = phone.type === 'mobile' ? 'CELL' : phone.type === 'work' ? 'WORK' : 'HOME';
        lines.push(`TEL;TYPE=${type}:${phone.number}`);
      }
    }

    // Add addresses
    if (contact.addresses) {
      for (const address of contact.addresses) {
        const type = address.type === 'work' ? 'WORK' : 'HOME';
        lines.push(`ADR;TYPE=${type}:;;${address.street};${address.city};${address.state};${address.zip};${address.country}`);
      }
    }

    // Add organization
    if (contact.company) {
      lines.push(`ORG:${contact.company}`);
    }

    if (contact.jobTitle) {
      lines.push(`TITLE:${contact.jobTitle}`);
    }

    lines.push('END:VCARD');

    return lines.join('\r\n');
  }

  /**
   * Get adapter name
   */

  /**
   * Check if adapter is available
   */
  async isAvailable(): Promise<boolean> {
    return !!this.interactr;
  }
}
